import { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { Box, Checkbox, CircularProgress, Input, Snackbar, Typography } from '@mui/joy';
import { usePatchIngredientMutation } from '@/features/ingredients/api';
import { SubcategorySelect } from '@/features/categories/components/SubcategorySelect';
import { ingredientSchema } from '@/features/ingredients/schema';
import { statusCodeToMessage } from '@/utils/errors';

// Tracks which single cell (by "<ingredientId>:<field>") is currently open for
// editing, and which one (if any) has a save in flight -- both shared across
// every EditableIngredientCell via context rather than local state, so opening
// or saving one cell can gate what every other cell is allowed to do.
// Without the shared activeKey, clicking around the table could leave several
// cells open, silently discarding whatever those other drafts had in them.
// Without the shared savingKey, starting a new edit while a previous cell's
// save is still in flight would abandon that cell mid-save: it snaps back to
// showing its pre-edit value (the real one hasn't come back from the server
// yet) and stays that way until something else happens to refetch it.
const ActiveCellContext = createContext({ activeKey: null, setActiveKey: () => {}, savingKey: null, setSavingKey: () => {} });

export function ActiveCellProvider({ children }) {
  const [activeKey, setActiveKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  return (
    <ActiveCellContext.Provider value={{ activeKey, setActiveKey, savingKey, setSavingKey }}>
      {children}
    </ActiveCellContext.Provider>
  );
}

const ellipsisSx = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' };

// A single editable cell in the ingredients table. Click a text/number/select
// cell to turn it into an input; whatever is in it when it loses focus (blur,
// Enter, or a select's own onChange) is what gets saved -- there's no separate
// confirm step. Escape reverts without saving. Checkbox fields skip the
// click-to-edit gate entirely and save the instant they're toggled, since a
// checkbox is already a one-click control.
//
// `editable` defaults to true; pass false to render a plain, non-interactive
// cell (used for the name column on mobile, where a table isn't a great place
// to open a keyboard-driven text edit).
export function EditableIngredientCell({ ingredient, field, type = 'text', displayValue, editable = true }) {
  const [patchIngredient] = usePatchIngredientMutation();
  const [errorMessage, setErrorMessage] = useState(null);
  const { activeKey, setActiveKey, savingKey, setSavingKey } = useContext(ActiveCellContext);
  const cellKey = `${ingredient.id}:${field}`;
  const isEditing = activeKey === cellKey;
  const isSaving = savingKey === cellKey;
  const [draft, setDraft] = useState('');
  const skipBlurSave = useRef(false);
  const selectWrapperRef = useRef(null);

  // A Select opened via defaultListboxOpen (see below) never gets real DOM
  // focus into its listbox -- MUI explicitly skips that "to prevent focus
  // stealing" when a select starts out already open -- so its own blur-driven
  // click-away closing never fires. Closing on an outside click has to be
  // done by hand instead. Clicks inside the trigger, or inside the listbox
  // itself (which is portalled outside selectWrapperRef, hence the
  // role="listbox" check), are left alone.
  useEffect(() => {
    if (type !== 'select' || !isEditing) return undefined;

    function handleDocumentMouseDown(event) {
      if (selectWrapperRef.current?.contains(event.target)) return;
      if (event.target.closest('[role="listbox"]')) return;
      if (!isSaving) setActiveKey(null);
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [type, isEditing, isSaving, setActiveKey]);

  async function save(rawValue) {
    let value;
    try {
      value = await yup.reach(ingredientSchema, field).validate(rawValue);
    } catch (err) {
      setErrorMessage(err.message);
      return false;
    }

    setSavingKey(cellKey);
    const result = await patchIngredient({ id: ingredient.id, data: { [field]: value } });
    setSavingKey(null);

    if (result.error) {
      setErrorMessage(statusCodeToMessage(result.error.status));
      return false;
    }

    return true;
  }

  const errorSnackbar = (
    <Snackbar
      color="danger"
      variant="soft"
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={!!errorMessage}
      onClose={() => setErrorMessage(null)}
    >
      {errorMessage}
    </Snackbar>
  );

  if (type === 'checkbox') {
    return (
      <>
        <Checkbox
          size="sm"
          checked={!!ingredient[field]}
          disabled={isSaving}
          onChange={(e) => save(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        />
        {errorSnackbar}
      </>
    );
  }

  function startEditing(e) {
    if (!editable || savingKey) return;
    e.stopPropagation();
    // A stale `true` here (left over from an Escape whose blur never fired
    // because the input was unmounted rather than naturally losing focus)
    // would otherwise silently swallow this cell's very next real save.
    skipBlurSave.current = false;
    setDraft(ingredient[field] ?? '');
    setActiveKey(cellKey);
  }

  async function handleBlur() {
    if (skipBlurSave.current) {
      skipBlurSave.current = false;
      return;
    }

    const value = type === 'number' && draft === '' ? null : draft;
    const original = ingredient[field] ?? '';
    if (value === original) {
      setActiveKey(null);
      return;
    }

    const saved = await save(value);
    if (saved) setActiveKey(null);
    // On failure the cell stays open (activeKey unchanged) with the error
    // shown, so clicking back in re-focuses the same draft to fix it.
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      skipBlurSave.current = true;
      setActiveKey(null);
    }
  }

  async function handleSelectChange(newValue) {
    const saved = await save(newValue);
    if (saved) setActiveKey(null);
  }

  if (!isEditing) {
    const shown = displayValue !== undefined ? displayValue : ingredient[field];
    return (
      <Typography
        level="body-sm"
        onClick={editable ? startEditing : undefined}
        sx={{ cursor: editable ? 'pointer' : 'default', ...ellipsisSx }}
      >
        {shown === '' || shown == null ? '—' : shown}
      </Typography>
    );
  }

  if (type === 'select') {    
    return (
      <>
        <Box ref={selectWrapperRef}>
          <SubcategorySelect
            value={ingredient[field]}
            onChange={handleSelectChange}
            onClose={() => { if (!isSaving) setActiveKey(null); }}
            onKeyDown={(e) => { if (e.key === 'Escape' && !isSaving) setActiveKey(null); }}
            size="sm"
            autoFocus
            defaultListboxOpen
            slotProps={{ listbox: { placement: 'bottom-start' } }}
            disabled={isSaving}
            endDecorator={isSaving ? <CircularProgress size="sm" /> : null}
            sx={{ width: '100%' }}
          />
        </Box>
        {errorSnackbar}
      </>
    );
  }

  return (
    <>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        autoFocus
        size="sm"
        type={type}
        disabled={isSaving}
        slotProps={type === 'number' ? { input: { min: 0, step: 0.01 } } : undefined}
        sx={{ width: '100%' }}
        endDecorator={isSaving ? <CircularProgress size="sm" /> : null}
      />
      {errorSnackbar}
    </>
  );
}
